import React, { useEffect, useRef, useState } from "react";
import {
    Box, CssBaseline, Breadcrumbs, Link, Typography, Stack, Button,
    Paper, Grid, TextField, MenuItem, CircularProgress, Divider,
    Snackbar, Alert, Card, CardContent, InputAdornment
} from "@mui/material";
import {
    NavigateNext, ArrowBack, Save, Clear, Person, LocationCity,
    Phone, Badge, Stars, FamilyRestroom, School
} from '@mui/icons-material';
import { Form, Formik } from "formik";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../component/header/Header";

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    accent: '#FFC107',
    accentDark: '#FFA000',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037',
    white: '#FFFFFF',
    filterBg: "#FFF9E6"
};

const EditParentScreen = () => {
    const { id } = useParams();
    const navigation = useNavigate();
    const formikRef = useRef();

    const [loading, setLoading] = useState(true);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [parentDetail, setParentDetail] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        getParentDetail();
        localStorage.setItem("current_tab", "Parent");
    }, [id]);

    const getParentDetail = async () => {
        setLoading(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/parents/${id}`);
            const resJson = await response.json();
            if (resJson.status === 1) {
                setParentDetail(resJson.data);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (values) => {
        setLoadingSubmit(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/parents/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            });
            const resJson = await response.json();

            if (resJson.status === 1) {
                setSnackbar({ open: true, message: "Parent details updated successfully!", severity: "success" });
                setTimeout(() => navigation("/Portal/Parent"), 1500);
            } else {
                setSnackbar({ open: true, message: resJson.message, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Update failed. Try again.", severity: "error" });
        } finally {
            setLoadingSubmit(false);
        }
    };

    const validationSchema = yup.object({
        fullname: yup.string().required("Parent Name is required"),
        mobileNo: yup.string().required("Mobile Number is required"),
        status: yup.string().required("Status is required"),
        point: yup.number().typeError("Points must be a number").required("Points are required")
    });

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
            <CircularProgress sx={{ color: colors.accent }} />
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

                {/* Navigation */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 1 }}>
                            <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                            <Link underline="hover" color="inherit" onClick={() => navigation("/Portal/Parent")} sx={{ cursor: 'pointer' }}>Parents</Link>
                            <Typography color="text.primary">Edit Parent</Typography>
                        </Breadcrumbs>
                        <Header title="Edit Parent Profile" subtitle={`Managing record for ${parentDetail?.fullname}`} />
                    </Box>
                    <Button startIcon={<ArrowBack />} onClick={() => navigation("/Portal/Parent")} color="inherit">Back</Button>
                </Stack>

                <Formik
                    innerRef={formikRef}
                    enableReinitialize={true}
                    initialValues={{
                        fullname: parentDetail?.fullname || "",
                        mobileNo: parentDetail?.mobileNo || "",
                        location: parentDetail?.location || "pj",
                        point: parentDetail?.point || "0",
                        status: parentDetail?.status || "A",
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleUpdate}
                >
                    {({ values, errors, touched, handleChange, handleBlur }) => (
                        <Form>
                            <Grid container spacing={3}>

                                {/* --- LEFT: PARENT INFO --- */}
                                <Grid item xs={12} md={8}>
                                    <Paper elevation={0} sx={{ p: 3, bgcolor: colors.filterBg, borderRadius: 3, border: '1px solid #F0E6CC' }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                                            <Person sx={{ color: colors.textMain }} />
                                            <Typography variant="h6" fontWeight="bold">Personal Information</Typography>
                                        </Stack>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" fontWeight="bold" mb={1}>Parent Name</Typography>
                                                <TextField fullWidth name="fullname" value={values.fullname} onChange={handleChange} onBlur={handleBlur}
                                                    error={touched.fullname && !!errors.fullname} helperText={touched.fullname && errors.fullname}
                                                    size="small" sx={{ bgcolor: 'white' }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body2" fontWeight="bold" mb={1}>Mobile Number</Typography>
                                                <TextField fullWidth name="mobileNo" value={values.mobileNo} onChange={handleChange} onBlur={handleBlur}
                                                    error={touched.mobileNo && !!errors.mobileNo} helperText={touched.mobileNo && errors.mobileNo}
                                                    size="small" sx={{ bgcolor: 'white' }}
                                                    InputProps={{ startAdornment: <Phone fontSize="small" sx={{ mr: 1, color: 'gray' }} /> }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body2" fontWeight="bold" mb={1}>Location</Typography>
                                                <TextField select fullWidth name="location" value={values.location} onChange={handleChange}
                                                    size="small" sx={{ bgcolor: 'white' }}
                                                    InputProps={{ startAdornment: <LocationCity fontSize="small" sx={{ mr: 1, color: 'gray' }} /> }}
                                                >
                                                    <MenuItem value="PJ">PJCC</MenuItem>
                                                    <MenuItem value="PD">Pantai Dalam</MenuItem>
                                                </TextField>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* --- RIGHT: ACCOUNT INFO --- */}
                                <Grid item xs={12} md={4}>
                                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #F0E6CC', borderRadius: 3, height: '100%' }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                                            <Badge sx={{ color: colors.accent }} />
                                            <Typography variant="h6" fontWeight="bold">Account & Points</Typography>
                                        </Stack>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold" mb={1}>Loyalty Points</Typography>
                                                <TextField fullWidth name="point" type="number" value={values.point} onChange={handleChange} size="small"
                                                    InputProps={{ startAdornment: <Stars fontSize="small" sx={{ mr: 1, color: colors.accent }} /> }}
                                                />
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold" mb={1}>Account Status</Typography>
                                                <TextField select fullWidth name="status" value={values.status} onChange={handleChange} size="small">
                                                    <MenuItem value="A">Active</MenuItem>
                                                    <MenuItem value="I">Inactive</MenuItem>
                                                </TextField>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Grid>

                                {/* --- BOTTOM: FAMILY OVERVIEW --- */}
                                <Grid item xs={12}>
                                    <Card elevation={0} sx={{ border: '1px solid #E0E0E0', borderRadius: 3 }}>
                                        <CardContent>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <FamilyRestroom sx={{ color: colors.accent }} />
                                                    <Box>
                                                        <Typography variant="h6" fontWeight="bold">Family Management</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Parent ID: {id}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<School />}
                                                    onClick={() => navigation(`/Portal/Student`)}
                                                    sx={{ borderRadius: 2, textTransform: 'none' }}
                                                >
                                                    View Children in Student Directory
                                                </Button>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* --- SUBMIT ACTIONS --- */}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                                        <Button variant="outlined" onClick={() => navigation("/Portal/Parent")} startIcon={<Clear />} sx={{ borderRadius: 2 }}>
                                            Discard Changes
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={loadingSubmit}
                                            startIcon={loadingSubmit ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                            sx={{ bgcolor: colors.accent, color: 'black', px: 6, borderRadius: 2, '&:hover': { bgcolor: colors.accentDark } }}
                                        >
                                            {loadingSubmit ? "Saving..." : "Update Profile"}
                                        </Button>
                                    </Stack>
                                </Grid>

                            </Grid>
                        </Form>
                    )}
                </Formik>

                <Snackbar
                    open={snackbar.open} autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>{snackbar.message}</Alert>
                </Snackbar>

            </Box>
        </Box>
    );
};

export default EditParentScreen;